import { Client, Team, useApiCall, useApiEndpoint } from "@jane/lib/src/api";
import { ContentContainer } from "@jane/lib/src/components/content/ContentContainer";
import { Loading } from "@jane/lib/src/components/Loading";
import React, { ReactNode, useMemo } from "react";
import { useClientData } from "../../resources/ClientContext";
import { useTeamData } from "../../resources/TeamContext";
import DynamicDataTable from "../../../../lib/src/components/data-table/DynamicDataTable";
import { HoverHint } from "@jane/lib/src/components/content/HoverHint";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { NavLink } from "react-router-dom";
import { PageHeader } from "@jane/lib/src/components/content/PageHeader";
import moment from "moment/moment";
import { config } from "../../config";
import { usePermissions } from "../../resources/PermissionsHook";
import { getRelatedTeamIds, useTeamMap } from "../../components/team-select";
import { faCircleExclamationCheck } from "@fortawesome/pro-solid-svg-icons";

interface ClientWithTeamAndFullName extends Client {
  team_name: string;
  full_name: string;
}

export default function ClientsListPage(): ReactNode {
  const { clients, isLoading } = useClientData();
  const { teams, isLoading: loadingTeams } = useTeamData();

  const { teams: teamsApi } = useApiCall(config);
  const { canListTeams } = usePermissions();
  const { resource: teamResource, isLoading: loadingTeam } = useApiEndpoint(() => teamsApi.myTeam(), canListTeams);
  const { resource: secondaryTeams, isLoading: loadingSecondaryTeams } = useApiEndpoint(
    () => teamsApi.myTeams(),
    canListTeams,
  );
  return (
    <ContentContainer size={"lg"}>
      <PageHeader>Cliënten</PageHeader>

      <Loading loading={isLoading || loadingTeam || loadingTeams || loadingSecondaryTeams}>
        {!!clients && (
          <FilteredClientTable
            clients={clients}
            teams={teams}
            primaryTeam={teamResource!}
            secondaryTeams={secondaryTeams || []}
          />
        )}
      </Loading>
    </ContentContainer>
  );
}

function FilteredClientTable(props: {
  clients: Client[];
  teams: Team[];
  primaryTeam: Team | null;
  secondaryTeams: Team[];
}): ReactNode {
  const teamMap: { [id: number]: Team } = useMemo(() => {
    let teamMap: { [id: number]: Team } = {};
    props.teams.forEach((team) => (teamMap[team.id] = team));
    return teamMap;
  }, [props.teams]);

  const enrichedClients = useMemo(
    () =>
      props.clients.map((client: Client): ClientWithTeamAndFullName => {
        return {
          ...client,
          team_name: client.team_id !== null && teamMap[client.team_id] ? teamMap[client.team_id].name : "-",
          full_name: `${client.given_name} ${client.family_name}`,
        };
      }),
    [props.clients, teamMap],
  );

  return (
    <div>
      <ClientTable
        clients={enrichedClients}
        teams={props.teams}
        primaryTeam={props.primaryTeam}
        secondaryTeams={props.secondaryTeams}
      />
    </div>
  );
}

function ClientTable({
  clients,
  teams,
  primaryTeam,
  secondaryTeams,
}: {
  clients: ClientWithTeamAndFullName[];
  teams: Team[];
  primaryTeam: Team | null;
  secondaryTeams: Team[];
}): ReactNode {
  const teamMap = useTeamMap(teams, primaryTeam, secondaryTeams);
  const { canListAllTeams, canLimitedListAllTeams } = usePermissions();
  const teamOptionsMap: Map<number, [string, number]> = useMemo(() => {
    return new Map([
      [-2, ["Alle", 0]],
      [-1, ["Mijn teams", 0]],
      ...teamMap
        .filter((teams) => teams[0] !== undefined)
        .map<[Team, number]>((teams) => [teams.slice(-1)[0], teams.length])
        .map<[number, [string, number]]>(([team, indentation]) => [team.id, [team.name, indentation]]),
    ]);
  }, [teamMap]);
  const teamFilterOptions: [string, string][] = useMemo(() => {
    return Array.from(teamOptionsMap)
      .filter(([id]: [number, [string, number]]): boolean => {
        return !(canListAllTeams && id === -1);
      })
      .map(([id, [name, indentation]]: [number, [string, number]]): [string, string] => {
        return [id.toString(), (canListAllTeams ? "\xA0\xA0".repeat(indentation) : "") + name];
      });
  }, [teamOptionsMap, canListAllTeams]);
  /** Used to look up related teams */
  const relatedTeamsMap: { [teamId: string]: string[] } = useMemo(() => {
    return Object.fromEntries(
      teams.map((team) => {
        return [team.id.toString(), getRelatedTeamIds(team, teamMap).map((id) => id.toString())];
      }),
    );
  }, [teamMap, teams]);
  return (
    <DynamicDataTable<ClientWithTeamAndFullName>
      searchableProperties={["client_number", "family_name", "full_name", "preferred_username", "team_name"]}
      placeholder={"Geen cliënten"}
      compact
      availableFilters={[
        {
          title: "Sync status",
          property: "sub",
          options: {
            all: "Alle",
            synced: "Jane account",
            sync_error: "Niet in systeem",
          },
          defaultValue: "all",
          filterCallback: (row, filter) => {
            if (filter === "synced") {
              return !!row.sub;
            } else if (filter === "sync_error") {
              return !row.sub;
            }
            return true;
          },
        },
        {
          title: "Activiteit",
          property: "last_activity",
          options: {
            all: "Alle",
            loggedin: "Ingelogd",
            last7days: "Laatste 7 dagen",
            last30days: "Laatste 30 dagen",
            longer30days: "Langer dan 30 dagen",
            never: "Nooit ingelogd",
          },
          defaultValue: "all",
          filterCallback: (row, filter) => {
            switch (filter) {
              case "loggedin":
                return row.last_activity !== null;
              case "last7days":
                return moment(row.last_activity).isAfter(moment().subtract(7, "days"));
              case "last30days":
                return moment(row.last_activity).isAfter(moment().subtract(1, "month"));
              case "longer30days":
                return moment(row.last_activity).isBefore(moment().subtract(1, "month"));
              case "never":
                return row.last_activity === null;
              default:
                return true;
            }
          },
        },
        {
          title: "Uitgenodigd",
          property: "invited_at",
          options: {
            all: "Alle",
            invited: "Uitgenodigd",
            not_invited: "Niet uitgenodigd",
          },
          defaultValue: "all",
          filterCallback: (row, filter) => {
            switch (filter) {
              case "invited":
                return row.invited_at !== null;
              case "not_invited":
                return row.invited_at === null;
              default:
                return true;
            }
          },
        },
        {
          title: "Team",
          property: "team_id",
          colSpan: 2,
          options: teamFilterOptions,
          defaultValue: canListAllTeams ? "-2" : "-1",
          filterCallback: (row, filter) => {
            return (
              filter === "-2" ||
              (filter === "-1" &&
                row.team_id !== null &&
                teamFilterOptions.map((t) => t[0]).includes(row.team_id.toString())) ||
              (row.team_id !== null && relatedTeamsMap[filter]?.includes(row.team_id.toString()))
            );
          },
        },
      ]}
      keyProperty={"id"}
      data={clients}
      columns={[
        {
          header: "Naam",
          property: "id",
          transform: (id, client) => {
            let prefix: string;
            switch (client.gender) {
              case "F":
                prefix = "Mevrouw";
                break;
              case "M":
                prefix = "Meneer";
                break;
              default:
                prefix = "Persoon";
            }
            return (
              <div className={"flex items-center"}>
                {client.sub ? (
                  <>
                    <HoverHint hint={"Jane Account"} key={id}>
                      <FontAwesomeIcon
                        icon={client.last_activity !== null ? faCheckCircle : faCircleExclamationCheck}
                        className={"text-green-400 text-lg"}
                      />
                    </HoverHint>
                  </>
                ) : (
                  <>
                    <HoverHint hint={"Niet in systeem"} key={id}>
                      <FontAwesomeIcon icon={faExclamationTriangle} className={"text-amber-500 text-lg"} />
                    </HoverHint>
                  </>
                )}
                <div className={"flex-1 ml-3"}>
                  <small className={"text-gray-500"}>{client.client_number}</small>
                  <div>
                    {prefix} <strong>{client.family_name}</strong>
                  </div>
                </div>
              </div>
            );
          },
        },
        { header: "Gebruikersnaam", property: "preferred_username" },
        { header: "Team", property: "team_name" },
        {
          header: "Laatst actief op",
          property: "last_activity",
          transform: (time, employee) => {
            if (time === null) {
              return <div className="flex items-center">Nooit ingelogd</div>;
            }
            const ts = moment(time);
            if (ts.isSame(moment(), "day")) {
              return <div className="flex items-center">Vandaag</div>;
            }
            if (ts.isSame(moment().subtract(1, "day"), "day")) {
              return <div className="flex items-center">Gisteren</div>;
            }
            if (ts.isSame(moment(), "week")) {
              return <div className="flex items-center">Deze week</div>;
            }
            return <div className="flex items-center">{ts.format("DD-MM-YYYY")}</div>;
          },
        },
        {
          header: "Is uitgenodigd",
          property: "invited_at",
          transform: (time) => {
            const ts = moment(time);
            return (
              <div className="flex items-center">{time !== null ? ts.format("DD-MM-YYYY") : "Niet uitgenodigd"}</div>
            );
          },
        },
        {
          header: "Acties",
          property: "id",
          transform: (id) => (
            <NavLink className={"text-brand-primary"} to={`/clients/${id}`}>
              Beheer
            </NavLink>
          ),
        },
      ]}
    />
  );
}
